import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IShare } from "@formant/core/model/IShare";

import { appStore, shareStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import { ShareFlow } from "../../Channel/ShareFlow";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Shares extends Component {
    @localSettingObservable("settings-shares-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-shares-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const columns: IDataTableColumn<IShare>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.message || ""} icon="share" />,
                sortBy: _ => _.message,
            },
            {
                header: "Created",
                row: _ =>
                    _.createdAt ? (
                        <RowDate date={_.createdAt} showTime={true} />
                    ) : (
                        "Unknown"
                    ),
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Created By",
                row: _ => _.userName,
                sortBy: _ => _.userName,
                hideBelow: "md",
            },
            {
                header: "Start",
                row: _ =>
                    _.scope.start !== undefined ? (
                        parseIsoDate(defined(_.scope.start)).getFullYear() <
                        1990 ? (
                            "Forever"
                        ) : (
                            <RowDate date={_.scope.start} showTime={true} />
                        )
                    ) : (
                        "None"
                    ),
                sortBy: _ => _.scope.start,
                hideBelow: "md",
            },
            {
                header: "End",
                row: _ =>
                    _.scope.end !== undefined ? (
                        parseIsoDate(defined(_.scope.end)).getFullYear() >
                        2100 ? (
                            "Forever"
                        ) : (
                            <RowDate date={_.scope.end} showTime={true} />
                        )
                    ) : (
                        "None"
                    ),
                sortBy: _ => _.scope.end,
                hideBelow: "md",
            },
            {
                header: "Expires",
                row: _ =>
                    _.expiration ? (
                        parseIsoDate(_.expiration).getTime() < Date.now() ? (
                            parseIsoDate(_.expiration).getTime() === 0 ? (
                                "Revoked"
                            ) : (
                                "Expired"
                            )
                        ) : (
                            <RowDate date={_.expiration} showTime={true} />
                        )
                    ) : (
                        "Never"
                    ),
                sortBy: _ => _.expiration || undefined,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ =>
                    _.expiration === null ||
                    (_.expiration !== undefined &&
                        parseIsoDate(_.expiration).getTime() >= Date.now()) ? (
                        <Actions share={_} onRevokeShare={this.onRevokeShare} />
                    ) : undefined,
            },
        ];

        let shares = shareStore.filter({
            hasModule: false,
        });
        if (shares) {
            shares = shares.sort((a, b) => {
                if (isExpired(a) && !isExpired(b)) {
                    return 1;
                }
                if (!isExpired(a) && isExpired(b)) {
                    return -1;
                }
                return (a.createdAt
                    ? parseIsoDate(a.createdAt).getTime()
                    : Number.MAX_SAFE_INTEGER) >
                    (b.createdAt
                        ? parseIsoDate(b.createdAt).getTime()
                        : Number.MAX_SAFE_INTEGER)
                    ? -1
                    : 1;
            });
        }
        const statHeaderGroups = [
            {
                subtotalQuantity: shares
                    ? shares.filter(
                          x =>
                              x.expiration === null ||
                              x.expiration === undefined,
                      ).length
                    : 0,
                subtotalLabel: "Unbounded shares",
            },
            {
                subtotalQuantity: shares
                    ? shares.filter(_ =>
                          _.expiration
                              ? parseIsoDate(_.expiration).getTime() >=
                                Date.now()
                              : false,
                      ).length
                    : 0,
                subtotalLabel: "Time-bound shares",
            },
            {
                subtotalQuantity: shares
                    ? shares.filter(_ =>
                          _.expiration
                              ? parseIsoDate(_.expiration).getTime() <
                                Date.now()
                              : false,
                      ).length
                    : 0,
                subtotalLabel: "Expired shares",
            },
        ];

        const { filter, onChangeFilter, sort, onChangeSort } = this;
        const filteredShares = applySimpleFilter(
            shares || [],
            [],
            getShareText,
            filter,
        );

        return (
            <SettingsPage
                title="Shares"
                extra={
                    <Help
                        text="This is a list of share links and codes that have been created. You can revoke them to prevent them from being used."
                        to="https://docs.formant.io/docs/share-to-external-users"
                    />
                }
            >
                {appStore.sharing && <ShareFlow />}
                <StatsHeader
                    icon="share"
                    loading={!shareStore}
                    totalQuantity={shares ? shares.length : 0}
                    totalLabel="Shares"
                    groups={statHeaderGroups}
                />
                <SimpleFilter
                    dimensions={[]}
                    rows={shares || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredShares}
                        columns={columns}
                        loading={shares === undefined}
                        noResultsText="No shares found"
                        onRowClicked={this.onEdit}
                        isRowClickable={this.isRowClickable}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = (share: IShare) => {
        if (!isExpired(share)) {
            window.open(window.location.origin + "/shares/" + share.code);
        }
    };

    private onRevokeShare = async (share: IShare) => {
        await shareStore.revoke(share);
        this.forceUpdate();
    };

    private isRowClickable = (share: IShare) => !isExpired(share);
}

function getShareText(template: IShare): string {
    return template.message || "";
}

function isExpired(share: IShare): boolean {
    return share.expiration
        ? parseIsoDate(share.expiration).getTime() < Date.now()
        : false;
}
